/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @classdesc helper class that is used to deal with OLAP
 */
define([], () => {
	class OlapHelperV2 {
		constructor(visualization) {
			this.visualization = visualization;
		}

		/**
		 * @desc Accepts olap column on slot
		 * @param {String} targetSlotId - target slot id
		 * @param {String} sourceSlotId - source slot id, it is undefined if dnd from metadata tree
		 * @param {Number} indexInSourceSlot - slot item index in source slot, it is undefined if dnd from metadata tree
		 * @param {Array} sourceColumns - metadata columns to be added into target slot
		 * @param {Number} indexInTargetSlot - position to add
		 * @param {Boolean} isReplace - if it is to replace the column in the position
		 * @return {Boolean} return True if the two lists of columns can be in the same slot
		 */
		acceptsOlapV2({ targetSlotId, sourceSlotId, indexInSourceSlot, sourceColumns, indexInTargetSlot, isReplace }) {
			// There are no restrictions on OLAP columns on non-crosstab visualizations
			if (this.visualization.getType() !== 'Crosstab') {
				return true;
			}
			const slotsAPI = this.visualization.getSlots();
			const targetSlotAPI = slotsAPI.getSlot(targetSlotId);
			const sourceSlotAPI = sourceSlotId? slotsAPI.getSlot(sourceSlotId) : undefined;
			const sourceMetadataColumns = sourceSlotAPI? sourceSlotAPI.getDataItemList().slice(indexInSourceSlot, indexInSourceSlot + 1).map(item => item.getMetadataColumn()) : sourceColumns;
			if (targetSlotAPI.getDefinition().getType() !== 'category' && (!sourceSlotAPI || sourceSlotAPI.getDefinition().getType() !== 'category')) {
				// OLAP restrictions only apply to categorical slots
				return true;
			}
			let hasOlapColumn = false;
			const olapMeasureColumn = sourceMetadataColumns.find(col => {
				const isOlapColumn = col.isOlapColumn();
				hasOlapColumn = hasOlapColumn || isOlapColumn;
				return isOlapColumn && col.getType() === 'fact';
			});
			if (olapMeasureColumn) {
				// OLAP measure column is not allowed to be dropped to category slot. Aggregation over measure sets is not supported.
				return false;
			}

			if (!hasOlapColumn) {
				// Need at least one OLAP column for OLAP restrictions to apply
				return true;
			}

			let accepts;
			if (sourceSlotAPI) {
				accepts = this._acceptsSlotItem({ slotsAPI, targetSlotAPI, sourceSlotAPI, sourceMetadataColumns, isReplace, indexInTargetSlot, indexInSourceSlot });
			} else {
				accepts = this._acceptsMetadataColumns({ slotsAPI, targetSlotAPI, sourceMetadataColumns });
			}

			const targetMetadataColumns = targetSlotAPI.getDataItemList().map(dataItem => dataItem.getMetadataColumn());
			const isSameSlot = targetSlotId === sourceSlotId;
			return accepts && this._acceptsColumnsInSlot({ targetMetadataColumns, sourceMetadataColumns, indexInTargetSlot, isReplace, isSameSlot, indexInSourceSlot });
		}

		/**
		 * Gets OLAP hierarchy by a metadata column
		 * @param  {Object} metadata column
		 * @return {Object} olap metadata hierarchy, return null if metadata column is not olap
		 */
		_getHierarchy(metadataColumn) {
			if (metadataColumn.isOlapColumn()) {
				if (metadataColumn.isHierarchy()) {
					return metadataColumn;
				} else if (metadataColumn.isLevel()) {
					return metadataColumn.getParent();
				} else if (metadataColumn.isProperty()) {
					const grandparent = metadataColumn.getParent().getParent();
					return grandparent && grandparent.isHierarchy() && grandparent;
				}
			}
			return null;
		}

		/**
		 * Check if two olap metadata columns are in the same hierarchy
		 * @param  {Object} metadata column 1
		 * @param  {Object} metadata column 2
		 * @return {Boolean} return true if the two metadata column are in the same hierarchy
		 */
		_inSameHierarchy(metadataColumn1, metadataColumn2) {
			if (metadataColumn1.isOlapColumn() && metadataColumn2.isOlapColumn()) {
				const h1 = this._getHierarchy(metadataColumn1);
				const h2 = this._getHierarchy(metadataColumn2);
				return h1 && h2 && h1.getId() === h2.getId();
			}
			return false;
		}

		_getHierarchies(metadataColumns) {
			const hierarchies = [];
			metadataColumns.forEach(col => {
				const hier = col && this._getHierarchy(col);
				if (hier) {
					if(!hierarchies.find(id => id === hier.getId())) {
						hierarchies.push(hier.getId());
					}
				}
			});
			return hierarchies;
		}
		/**
		 * @desc accepts a slot item
		 * @param {Object} slotsAPI - slots api
		 * @param {Object} targetSlotAPI - target slot api
		 * @param {Object} sourceSlotAPI - source slot api
		 * @param {Array} sourceMetadataColumns - metadata columns to be added into target slot
		 * @param {Number} indexInSourceSlot - slot item index in source slot, it is undefined if dnd from metadata tree
		 * @param {Number} indexInTargetSlot - position to add
		 * @param {Boolean} isReplace - if it is to replace the column in the position
		 * @return {Boolean} return True if a hierarchy is not on both col and row
		 */
		_acceptsSlotItem({ slotsAPI, targetSlotAPI, sourceSlotAPI, sourceMetadataColumns, isReplace, indexInTargetSlot, indexInSourceSlot }) {
			let metadataColumnsTargetSlot = targetSlotAPI.getDataItemList().map(dataItem => dataItem.getMetadataColumn());
			let metadataColumnsSourceSlot = sourceSlotAPI.getDataItemList().map(dataItem => dataItem.getMetadataColumn());
			let sourceMetadataColumn = sourceMetadataColumns[0]; //can only drag one slot item at a time
			if (isReplace) {
				const swapTarget = metadataColumnsTargetSlot[indexInTargetSlot];
				metadataColumnsTargetSlot[indexInTargetSlot] = sourceMetadataColumn;
				metadataColumnsSourceSlot[indexInSourceSlot] = swapTarget;
			} else {
				metadataColumnsTargetSlot.splice(indexInTargetSlot, 0, sourceMetadataColumn);
				metadataColumnsSourceSlot.splice(indexInSourceSlot, 1);
			}

			//get rest of category slot IDs
			const restSlotIDs = slotsAPI.getSlotList().filter(slot => slot.getDefinition().getId() !== targetSlotAPI.getDefinition().getId() &&
				slot.getDefinition().getId() !== sourceSlotAPI.getDefinition().getId() &&
				slot.getDefinition().getType() === 'category').map(s => s.getDefinition().getId());
			const slotsColumns = restSlotIDs.map(id => slotsAPI.getSlot(id).getDataItemList().map(dataItem => dataItem.getMetadataColumn()));

			const hierarchies = [];
			const isSameSlot = targetSlotAPI.getDefinition().getId() === sourceSlotAPI.getDefinition().getId();
			if (!isSameSlot) {
				if (targetSlotAPI.getDefinition().getType() === 'category') {
					slotsColumns.push(metadataColumnsTargetSlot);
				}
				if (sourceSlotAPI.getDefinition().getType() === 'category') {
					slotsColumns.push(metadataColumnsSourceSlot);
				}
			}
			return slotsColumns.every(columns => {
				const hierList = this._getHierarchies(columns);
				if (hierarchies.some(h => hierList.includes(h))) {
					return false;
				}
				hierarchies.push(...hierList);
				return true;
			});
		}

		/**
		 * @desc accepts a list of metadata columns from metadata tree
		 * @param {Object} slotsAPI - slots api
		 * @param {Object} targetSlotAPI - target slot api
		 * @param {Array} sourceMetadataColumns - metadata columns to be added into target slot
		 * @return {Boolean} return True if a hierarchy is not on both col and row
		 */
		_acceptsMetadataColumns({ slotsAPI, targetSlotAPI, sourceMetadataColumns }) {
			const categorySlots = slotsAPI.getSlotList().filter(slot => slot.getDefinition().getType() === 'category');
			const dataItemList = [];
			categorySlots.forEach(slot => {
				const items = slot.getDataItemList().filter(dataItem=>dataItem.getColumnId() !== '_multiMeasuresSeries');
				dataItemList.push(...items);
			});

			const projectedMetadataColumns = dataItemList.map(dataItem => dataItem.getMetadataColumn());
			const targetSlotDataItemList = targetSlotAPI.getDataItemList();
			const targetSlotMetadataColumnIds = targetSlotDataItemList.filter(dataItem => dataItem.getColumnId() !== '_multiMeasuresSeries').map(dataItem => dataItem.getColumnId());
			let columnsNotInTargetSlot =projectedMetadataColumns.filter(column => targetSlotMetadataColumnIds.indexOf(column.getId()) === -1);
			const hierarchyNotInTargetSlot =  columnsNotInTargetSlot.some(column =>
				sourceMetadataColumns.some(dragColumn => this._inSameHierarchy(dragColumn, column)));

			// Find any columns which will be in different slots from the target slot,
			// and which are in the same hierarchy as the source columns
			if (hierarchyNotInTargetSlot) {
				return false;
			}
			return true;
		}
		/**
		 * @desc Check if a list of metadata columns can be added into another list of columns in the same slot
		 * @param {Array} targetMetadataColumns - metadata columns in target slot
		 * @param {Array} sourceMetadataColumns - metadata columns to be added into target slot
		 * @param {Number} indexInTargetSlot - index in target slot to add
		 * @param {Number} indexInSourceSlot - slot item index in source slot, it is undefined if dnd from metadata tree
		 * @param {Boolean} isReplace - if it is to replace the column in the position
		 * @param {Boolean} IsSameSlot - if it is drag and drop in the same slot
		 * @return {Boolean} return True if the two lists of columns can be in the same slot
		 */
		_acceptsColumnsInSlot({ targetMetadataColumns, sourceMetadataColumns, indexInTargetSlot, isReplace, isSameSlot, indexInSourceSlot }) {
			//check if OLAP columns are in natural order and consecutive in slot
			if (!targetMetadataColumns || !sourceMetadataColumns || indexInTargetSlot === undefined || isReplace === undefined) {
				return false;
			}
			const targetSlotMetadataColumns = [...targetMetadataColumns];
			if (indexInTargetSlot < 0) {
				indexInTargetSlot = targetSlotMetadataColumns.length;
			}
			if (isReplace) {
				if (isSameSlot) {
					//swap
					//can only drag one slot item, so actually sourceMetadataColumns.length is 1
					const items = targetSlotMetadataColumns.splice(indexInTargetSlot, 1, ...sourceMetadataColumns);
					targetSlotMetadataColumns.splice(indexInSourceSlot, 1, ...items);
				} else {
					targetSlotMetadataColumns.splice(indexInTargetSlot, 1, ...sourceMetadataColumns);
				}
			} else {
				if (isSameSlot) {
					//if dnd in the same slot, remove dragged item from target slot columns
					targetSlotMetadataColumns.splice(indexInSourceSlot, 1);
					if (indexInSourceSlot < indexInTargetSlot) {
						indexInTargetSlot--;
					}
				}
				targetSlotMetadataColumns.splice(indexInTargetSlot, 0, ...sourceMetadataColumns);
			}
			return this._isColumnOrderValidInSlot(targetSlotMetadataColumns);
		}

		_isColumnOrderValidInSlot(metadataColumns) {
			const hierarchyMap = {};
			metadataColumns.forEach((col, index) => {
				const hierarchy = this._getHierarchy(col);
				if (hierarchy) {
					if (hierarchyMap[hierarchy.getId()]) {
						hierarchyMap[hierarchy.getId()].metadataColumns.push({
							metadataColumn: col,
							index: index
						});
					} else {
						hierarchyMap[hierarchy.getId()] = {
							metadataColumns: [{
								metadataColumn: col,
								index: index
							}],
							hierarchy: hierarchy
						};
					}
				}
			});

			return this._isHierarchyInNaturalOrderAndConsecutiveNestingLevels(hierarchyMap);
		}

		_isHierarchyInNaturalOrderAndConsecutiveNestingLevels(hierarchyMap) {
			const hierarchies = Object.values(hierarchyMap);
			return !hierarchies.some(hierarchy => {
				const hierarchyItemIds = hierarchy.hierarchy.getHierarchyLevelIds();
				// Find natural order index for the column in each position
				let indexes = hierarchy.metadataColumns.map(col => {
					let metadataColumn = col.metadataColumn;
					if (metadataColumn.isHierarchy()) {
						return -1;
					} else if (metadataColumn.isProperty()){
						metadataColumn = metadataColumn.getParent();
					}
					return hierarchyItemIds.findIndex(id => metadataColumn.getId() === id);
				});
				if (!this._isDescSorted(indexes)) {
					return true;
				}
				indexes = hierarchy.metadataColumns.map(col => col.index);
				if (!this._isHierarchyInConsecutiveNestingLevels(indexes)) {
					return true;
				}
			});
		}

		_isHierarchyInConsecutiveNestingLevels(indexes) {
			let consecutive = true;
			for (let i = 0; i <= indexes.length - 2; i++) {
				if (indexes[i] + 1 !== indexes[i+1]) {
					consecutive = false;
					break;
				}
			}
			return consecutive;
		}

		_isDescSorted(indexes) {
			let sorted = true;
			for (let i = 0; i < indexes.length - 1; i++) {
				if (indexes[i] > indexes[i+1]) {
					sorted = false;
					break;
				}
			}
			return sorted;
		}
	}
	return OlapHelperV2;
});
